import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AlertBox, StandardForm } from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { __, __url, getSdpUrl } from '@99designs/i18n';
import { StepLayout } from '@99designs/swan-wizard';
import { usePage } from '@99designs/tracking';
import { useBriefContext } from '../BriefContext';
import { BriefFormProvider } from '../BriefContext/BriefFormContext';
import { MultiStep } from '../MultiStepSwanWizard/MultiStep';
import { FormInput } from './BriefForm';
import { ContinueButton } from './ContinueButton';
import { FieldGroup, fieldGroups, useFormGroups } from './FieldGroup';
import { FieldProcessingProvider } from './FieldProcessingProvider';
import { useProductOptions } from './FormFields/ClientConfiguredFields/ProductOptions';
import HiddenFields from './HiddenFields';
import { Information } from './Information';
import { IntroStep } from './IntroStep';
import { BriefQuery } from './brief.generated';
import { useGetSubmitMultiStep } from './getSubmit';
import { useRegisterWithTracking } from './useRegisterWithTracking';

function useIsStepValid(
    groups: ReturnType<typeof fieldGroups>,
    trigger: (name?: string | string[]) => Promise<boolean>,
    formErrors: Record<string, any>,
    setFocus: (name: string) => void,
    watch: (name: string) => any
) {
    const { validate, setAllProductOptionsTouched } = useProductOptions();
    const [isValid, setIsValid] = React.useState(true);
    // validateCount is used to ensure the effect is run on every validation
    const [validateCount, setValidateCount] = React.useState(0);

    useEffect(() => {
        if (!isValid) {
            try {
                setFocus(Object.keys(formErrors)[0]);
            } catch (e) {
                /**
                 * Sometimes setFocus can throw an error if we're registering the form to an input
                 * Examples like FileField and IntelligentStylesField will fail when this is called
                 */
            }
        }
    }, [formErrors, isValid, setFocus, validateCount]);

    return useCallback(
        async (currentStep: number) => {
            // We subtract 1 from the current step because we start the steps at 1
            const group = groups[currentStep - 1];
            if (!group) {
                return true;
            }

            // We assume here that product options is always in a group of its own
            const productOptionField = group.fields
                .filter((field) => field.id === 'productOptions')
                .pop();
            if (productOptionField) {
                const isValid = await validate();
                if (!isValid) {
                    setAllProductOptionsTouched();
                    return false;
                }
            }

            const isValid = await trigger(group.fields.map((f) => f.id));

            // Special case: if DesignLive is selected, we need to ensure appointmentTime is set
            // This manual check because we can't rely on react-hook-form to do this for us as the component
            // may not be rendered yet
            const requestType = watch('requestType');
            const appointmentTime = watch('appointmentTime');
            if (
                isValid &&
                group.id === 'reviewingYourDesign' &&
                requestType === 'DesignLive' &&
                !appointmentTime
            ) {
                return false;
            }

            setIsValid(isValid);
            setValidateCount((prev) => prev + 1);

            return isValid;
        },
        [groups, trigger, validate, setAllProductOptionsTouched, watch]
    );
}

export interface MultiStepBriefFormProps {
    brief: BriefQuery['brief'];
    locale?: string;
}

function isSizeMissingForCareExpress(
    workflow: string | null | undefined,
    printOutcome: { options?: { encoded: string } | null } | null | undefined
): boolean {
    if (workflow !== 'CARE_EXPRESS') {
        return false;
    }

    if (!printOutcome?.options?.encoded) {
        return true;
    }

    try {
        const decoded = JSON.parse(printOutcome.options.encoded);
        const hasSize = Object.keys(decoded).some((key) => key.toLowerCase() === 'size');
        return !hasSize;
    } catch (e) {
        bugtracker.notify(
            new Error('Failed to parse printOutcome.options.encoded for CARE_EXPRESS workflow')
        );
        return true;
    }
}

export const MultiStepBriefForm = ({ brief, locale }: MultiStepBriefFormProps) => {
    const { intro, groups, hiddenFields, workEntityField } = useFormGroups(brief);
    const { pageData } = usePage();
    const getSubmitMultiStep = useGetSubmitMultiStep();
    const { product, logomakerAsset } = useBriefContext();

    const mpvId =
        brief.request?.expertService?.printProduct?.mpvId || brief.product?.fulfilmentMpvId || '';
    const esmpvId = brief.request?.expertService?.serviceProduct?.mpvId || '';

    const methods = useForm<FormInput>({
        shouldUnregister: true,
    });
    const {
        register,
        setValue,
        watch,
        handleSubmit,
        clearErrors,
        setError,
        formState: { errors: formErrors, dirtyFields },
        getValues,
        trigger,
        setFocus,
    } = methods;

    const registerWithTracking = useRegisterWithTracking(
        brief,
        register,
        getValues,
        dirtyFields,
        pageData
    );

    const isUploadAndGo = brief.request?.facts?.workflow === 'CARE_EXPRESS';

    const shouldShowSizeError = isSizeMissingForCareExpress(
        brief.request?.facts?.workflow,
        brief.request?.optionalFacts?.printOutcome
    );

    useEffect(() => {
        if (logomakerAsset && workEntityField) {
            bugtracker.notify(
                new Error(
                    'Both logomakerAsset and workEntityField are present in MultiStepBriefForm which is unexpected.'
                )
            );
        }
    }, [logomakerAsset, workEntityField]);

    const sizeErrorBanner = shouldShowSizeError ? (
        <AlertBox key="size-error" data-testid="size-missing-error" skin="warning" mt={4} mb={6}>
            {__(
                'Size not selected. Please return to the product page and select a size to submit a custom shape request.'
            )}
        </AlertBox>
    ) : null;

    const steps = [
        <IntroStep
            key="intro"
            product={product ?? null}
            startingAsset={logomakerAsset || workEntityField}
            isUploadAndGo={isUploadAndGo}
            group={intro}
            sizeErrorBanner={sizeErrorBanner}
            step={
                <FieldGroup
                    key={intro.id}
                    group={{
                        ...intro,
                        fields: intro.fields.filter((f) => f.__typename !== 'WorkEntityField'),
                    }}
                    register={registerWithTracking}
                    errors={formErrors}
                    setValue={setValue}
                    watch={watch}
                    clearErrors={clearErrors}
                    setError={setError}
                    isIntro={true}
                    titleFontSkin={'body-standard-bold'}
                    esmpvId={esmpvId}
                    locale={locale}
                />
            }
        />,
        ...groups.map((g, index) => (
            <StepLayout
                key={g.id}
                NextAction={
                    index === groups.length - 1
                        ? () => <ContinueButton showReview={brief.config.showReview} />
                        : undefined
                }
                Information={() => <Information product={product} />}
            >
                <FieldGroup
                    key={g.id}
                    group={g}
                    register={registerWithTracking}
                    errors={formErrors}
                    setValue={setValue}
                    watch={watch}
                    isIntro={false}
                    clearErrors={clearErrors}
                    setError={setError}
                    esmpvId={esmpvId}
                    locale={locale}
                />
            </StepLayout>
        )),
    ];
    const allGroups = useMemo(() => [intro, ...groups], [intro, groups]);
    const validate = useIsStepValid(allGroups, trigger, formErrors, setFocus, watch);

    if (brief.groups === undefined) {
        return null;
    }

    return (
        <StandardForm
            style={{ height: '100%' }}
            onSubmit={getSubmitMultiStep(handleSubmit, brief.id)}
        >
            <FieldProcessingProvider>
                <BriefFormProvider briefId={brief.id} isUploadAndGo={isUploadAndGo}>
                    <FormProvider {...methods}>
                        <HiddenFields hiddenFields={hiddenFields} methods={methods} />
                        <MultiStep
                            steps={steps}
                            validate={validate}
                            title={
                                brief.request?.expertService?.title ||
                                brief.product?.title ||
                                __('Custom Design')
                            }
                            exitUrl={mpvId ? getSdpUrl(mpvId) : __url('/design')}
                            trackSteps
                        />
                    </FormProvider>
                </BriefFormProvider>
            </FieldProcessingProvider>
        </StandardForm>
    );
};
